import { setupShaders } from './shaders/setupShaders';
import { radixSortShaders } from './shaders/radixSortShaders';
import {
  lbvhResetRefitStateShader,
  lbvhRefitLeavesShader,
  lbvhInitStateShader,
  lbvhBuildTopologyShader,
  lbvhSeedInternalShader,
  lbvhRefitWaveShader,
  lbvhUpdateDispatchShader,
  lbvhFinalizeShader,
  lbvhQualityProbeShader,
  lbvhQualityFinalizeShader,
  lbvhSparsePrepareShader,
  lbvhSparseLeavesShader,
  lbvhSparseSeedShader,
  lbvhSparseWaveShader,
  lbvhSparseValidateShader,
  lbvhFallbackPrepareShader,
  lbvhFallbackResetShader,
  lbvhFallbackValidatePrepareShader,
  lbvhFallbackValidateShader,
  lbvhSparseFinalizeShader,
} from './shaders/lbvhShaders';
import { OneSweepSorter } from './sorting/OneSweepSorter.js';
import type { PhysicsSubmissionLedger } from '../physics/gpu/physicsSubmissionLedger';

const BUILD_WORKGROUP_SIZE = 256;
const RADIX_PASSES = 4;
const RADIX_WORKGROUP_SIZE = 256;
const RADIX_SIZE = 256;
const LBVH_REFIT_MAX_ITERATION_FACTOR = 4;
const UNIFORM_BYTES = 16;
const UNIFORM_ALIGN = 256;
const UNIFORM_SETUP_OFFSET = 0 * UNIFORM_ALIGN;
const UNIFORM_MORTON_OFFSET = 1 * UNIFORM_ALIGN;
const UNIFORM_LBVH_OFFSET = 2 * UNIFORM_ALIGN;
const UNIFORM_RADIX_BASE_OFFSET = 3 * UNIFORM_ALIGN;
const UNIFORM_SPARSE_OFFSET = (3 + RADIX_PASSES) * UNIFORM_ALIGN;
const SPARSE_UNIFORM_BYTES = 32;
const UNIFORM_SLOT_COUNT = 4 + RADIX_PASSES;

export const LBVHSorterType = {
  BUILTIN: 'builtin',
  ONESWEEP: 'onesweep',
} as const;

export type LBVHSorterTypeValue = typeof LBVHSorterType[keyof typeof LBVHSorterType];

type BuildBuffers = {
  sceneBounds: GPUBuffer;
  mortonCodes: GPUBuffer;
  mortonCodesAlt: GPUBuffer;
  clusterIdx: GPUBuffer;
  clusterIdxAlt: GPUBuffer;
  hplocState: GPUBuffer;
  activeList: GPUBuffer;
  parentIdx: GPUBuffer;
  refitVisitCount: GPUBuffer;
  activeCount0: GPUBuffer;
  activeCount1: GPUBuffer;
  refitDepth: GPUBuffer;
  qualityState: GPUBuffer;
  refitMarks: GPUBuffer;
  refitState: GPUBuffer;
  indirectDispatch: GPUBuffer;
  fallbackGateDispatch: GPUBuffer;
  bvh2Nodes: GPUBuffer;
  nodeCounter: GPUBuffer;
  groupCounts: GPUBuffer;
  groupPrefix: GPUBuffer;
  globalDigitCount: GPUBuffer;
  digitOffsets: GPUBuffer;
  uniforms: GPUBuffer;
};

type Pipelines = {
  setupBounds: GPUComputePipeline;
  setupMorton: GPUComputePipeline;
  lbvhResetRefitState: GPUComputePipeline;
  lbvhRefitLeaves: GPUComputePipeline;
  radixHistogram: GPUComputePipeline;
  radixWorkgroupScan: GPUComputePipeline;
  radixScan: GPUComputePipeline;
  radixScatter: GPUComputePipeline;
  lbvhInitState: GPUComputePipeline;
  lbvhBuildTopology: GPUComputePipeline;
  lbvhSeedInternal: GPUComputePipeline;
  lbvhRefitWave: GPUComputePipeline;
  lbvhUpdateDispatch: GPUComputePipeline;
  lbvhFinalize: GPUComputePipeline;
  lbvhQualityProbe: GPUComputePipeline;
  lbvhQualityFinalizeBaseline: GPUComputePipeline;
  lbvhQualityFinalizeSample: GPUComputePipeline;
  lbvhSparsePrepare: GPUComputePipeline;
  lbvhSparseLeaves: GPUComputePipeline;
  lbvhSparseSeed: GPUComputePipeline;
  lbvhSparseWave: GPUComputePipeline;
  lbvhSparseValidate: GPUComputePipeline;
  lbvhFallbackPrepare: GPUComputePipeline;
  lbvhFallbackReset: GPUComputePipeline;
  lbvhFallbackValidatePrepare: GPUComputePipeline;
  lbvhFallbackValidate: GPUComputePipeline;
  lbvhSparseFinalize: GPUComputePipeline;
};

type SharedPipelineEntry = {
  ready: Pipelines | null;
  promise: Promise<Pipelines> | null;
};

// Both alternating BVH builders use identical immutable pipelines on the same
// device. Compile them once per GPUDevice instead of launching 54 cold shader
// and pipeline creations during startup.
const SHARED_PIPELINES_BY_DEVICE = new WeakMap<GPUDevice, SharedPipelineEntry>();

type BuildOptions = {
  positionBuffer: GPUBuffer;
  indexBuffer: GPUBuffer;
  primCount: number;
  positionStride?: number;
  useFlatten?: boolean;
  waitForGpuCompletion?: boolean;
  sampleQuality?: boolean;
  submissionFrameId?: number;
};

type SparseRefitOptions = BuildOptions & {
  dirtyLeafBuffer: GPUBuffer;
  dirtyLeafCapacity: number;
  debugBuffer: GPUBuffer;
  debugCounterOffset?: number;
  forceFullRefit?: boolean;
  resetQualityBaseline?: boolean;
};

export class GPULBVHBuilder {
  private readonly device: GPUDevice;
  private readonly requestedSorterType: LBVHSorterTypeValue;
  private warnedNoSubgroups = false;

  private sorter: any = null;
  private sorterCapacity = 0;

  private pipelines: Pipelines | null = null;
  private pipelineInitPromise: Promise<void> | null = null;
  private pipelineInitGeneration = 0;
  private buildBuffers: BuildBuffers | null = null;
  private bufferCapacity = 0;
  private sorterInitPromise: Promise<void> | null = null;
  private prewarmPromise: Promise<void> | null = null;
  private staticBindGroups: {
    setupBounds: GPUBindGroup | null;
    setupMorton: GPUBindGroup | null;
    lbvhResetRefitState: GPUBindGroup | null;
    lbvhRefitLeaves: GPUBindGroup | null;
    lbvhInitState: GPUBindGroup | null;
    lbvhBuildTopology: GPUBindGroup | null;
    lbvhSeedInternal: GPUBindGroup | null;
    lbvhRefitWave0to1: GPUBindGroup | null;
    lbvhRefitWave1to0: GPUBindGroup | null;
    lbvhUpdateDispatch0to1: GPUBindGroup | null;
    lbvhUpdateDispatch1to0: GPUBindGroup | null;
    lbvhFinalize: GPUBindGroup | null;
    lbvhQualityProbe: GPUBindGroup | null;
    lbvhQualityFinalizeBaseline: GPUBindGroup | null;
    lbvhQualityFinalizeSample: GPUBindGroup | null;
  } = {
    setupBounds: null,
    setupMorton: null,
    lbvhResetRefitState: null,
    lbvhRefitLeaves: null,
    lbvhInitState: null,
    lbvhBuildTopology: null,
    lbvhSeedInternal: null,
    lbvhRefitWave0to1: null,
    lbvhRefitWave1to0: null,
    lbvhUpdateDispatch0to1: null,
    lbvhUpdateDispatch1to0: null,
    lbvhFinalize: null,
    lbvhQualityProbe: null,
    lbvhQualityFinalizeBaseline: null,
    lbvhQualityFinalizeSample: null,
  };
  private staticBindGroupBuffers: {
    buildBuffers: BuildBuffers | null;
    position: GPUBuffer | null;
    index: GPUBuffer | null;
  } = {
    buildBuffers: null,
    position: null,
    index: null,
  };
  private sparseBindGroups: {
    prepare: GPUBindGroup | null;
    leaves: GPUBindGroup | null;
    seed: GPUBindGroup | null;
    wave0to1: GPUBindGroup | null;
    wave1to0: GPUBindGroup | null;
    validate: GPUBindGroup | null;
    fallbackPrepare: GPUBindGroup | null;
    fallbackReset: GPUBindGroup | null;
    fallbackValidatePrepare: GPUBindGroup | null;
    fallbackValidate: GPUBindGroup | null;
    finalize: GPUBindGroup | null;
  } = {
    prepare: null,
    leaves: null,
    seed: null,
    wave0to1: null,
    wave1to0: null,
    validate: null,
    fallbackPrepare: null,
    fallbackReset: null,
    fallbackValidatePrepare: null,
    fallbackValidate: null,
    finalize: null,
  };
  private sparseBindGroupBuffers: {
    buildBuffers: BuildBuffers | null;
    position: GPUBuffer | null;
    index: GPUBuffer | null;
    dirty: GPUBuffer | null;
    debug: GPUBuffer | null;
  } = {
    buildBuffers: null,
    position: null,
    index: null,
    dirty: null,
    debug: null,
  };

  private positionBuffer: GPUBuffer | null = null;
  private indexBuffer: GPUBuffer | null = null;
  private primCount = 0;
  private positionStride = 3;
  private topologyGeneration = 0;
  private measuredRefitIterations: number | null = null;
  private refitEpoch = 0;
  private disposed = false;
  private readonly submissionLedger?: PhysicsSubmissionLedger;

  constructor(
    device: GPUDevice,
    options: {
      sorterType?: LBVHSorterTypeValue;
      submissionLedger?: PhysicsSubmissionLedger;
    } = {},
  ) {
    this.device = device;
    // Builtin radix uses explicit pass boundaries for global synchronization.
    // Keep OneSweep opt-in only: its in-shader inter-workgroup wait relies on
    // a forward-progress guarantee that WebGPU does not provide.
    this.requestedSorterType = options.sorterType ?? LBVHSorterType.BUILTIN;
    this.submissionLedger = options.submissionLedger;
  }

  get bvh2Buffer(): GPUBuffer | null {
    return this.buildBuffers ? this.buildBuffers.bvh2Nodes : null;
  }

  get clusterIdxBuffer(): GPUBuffer | null {
    return this.buildBuffers ? this.buildBuffers.clusterIdx : null;
  }

  get parentIdxBuffer(): GPUBuffer | null {
    return this.buildBuffers ? this.buildBuffers.parentIdx : null;
  }

  /** Exact number of non-empty bottom-up refit waves measured for this tree. */
  get refitTreeDepth(): number | null {
    return this.measuredRefitIterations;
  }

  get qualityMetricsBuffer(): GPUBuffer | null {
    return this.buildBuffers ? this.buildBuffers.qualityState : null;
  }

  get maxNodeCount(): number {
    return this.primCount > 0 ? this.primCount * 2 : 0;
  }

  async prewarm(primCapacity: number): Promise<void> {
    if (this.prewarmPromise) {
      return this.prewarmPromise;
    }

    const target = Math.max(1, primCapacity | 0);
    this.prewarmPromise = (async () => {
      if (this.disposed) {
        return;
      }
      this.allocateBuffers(target);
      await this.ensurePipelines();
      if (this.disposed) {
        return;
      }
      await this.ensureSorter(target);
    })().finally(() => {
      this.prewarmPromise = null;
    });

    return this.prewarmPromise;
  }

  async buildAsyncFromGPUBuffers(options: BuildOptions): Promise<void> {
    if (this.disposed) {
      return;
    }
    const {
      positionBuffer,
      indexBuffer,
      primCount,
      positionStride = 3,
      waitForGpuCompletion = true,
    } = options;

    this.positionBuffer = positionBuffer;
    this.indexBuffer = indexBuffer;
    this.primCount = Math.max(0, primCount | 0);
    this.positionStride = positionStride;
    const topologyGeneration = ++this.topologyGeneration;
    this.measuredRefitIterations = null;

    if (this.prewarmPromise) {
      await this.prewarmPromise;
    }
    if (this.disposed) {
      return;
    }

    this.allocateBuffers(this.primCount);
    if (!this.pipelines) {
      const pipelineReady = this.ensurePipelines();
      // A later builder on the same device can adopt the shared pipeline set
      // synchronously. Preserve the observed-frame submit in that warm-cache
      // case; only a genuinely cold native compile yields here.
      if (!this.pipelines) {
        await pipelineReady;
        if (this.disposed) {
          return;
        }
      }
    }
    // The production builder uses the built-in radix path. Awaiting the async
    // sorter helper even when it immediately returns yields to a microtask and
    // moves the actual LBVH queue.submit outside the animation frame's submit
    // observer/fence scope. Only OneSweep genuinely needs asynchronous setup.
    if (this.shouldUseOneSweep()) {
      await this.ensureSorter(this.primCount);
      if (this.disposed) {
        return;
      }
    }
    this.initBuildState();
    this.ensureStaticBindGroups();

    if (this.primCount === 0 || !this.buildBuffers || !this.pipelines) {
      if (waitForGpuCompletion) {
        await this.device.queue.onSubmittedWorkDone();
      }
      return;
    }

    const useOneSweep = this.shouldUseOneSweep();
    const depthReadback = this.device.createBuffer({
      label: 'LBVH Refit Depth Readback',
      size: 4,
      usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
    });
    const encoder = this.device.createCommandEncoder({ label: 'LBVH Build Encoder' });
    encoder.clearBuffer(this.buildBuffers.refitDepth);
    encoder.clearBuffer(this.buildBuffers.qualityState);
    this.recordSetupPass(encoder, this.primCount);
    if (useOneSweep) {
      this.recordOneSweepSort(encoder, this.primCount);
    } else {
      this.recordBuiltinRadixSort(encoder, this.primCount);
    }
    this.recordLBVHPass(encoder, this.primCount);
    this.recordQualityPass(encoder, this.primCount, true);
    encoder.copyBufferToBuffer(this.buildBuffers.refitDepth, 0, depthReadback, 0, 4);
    const submitBuild = (): void => this.device.queue.submit([encoder.finish()]);
    if (this.submissionLedger) {
      this.submissionLedger.withActualSubmitScope(
        this.device.queue,
        {
          frameId: options.submissionFrameId,
          submissionClass: 'topology',
          label: 'lbvh-build',
        },
        submitBuild,
      );
    } else {
      submitBuild();
    }
    this.submissionLedger?.record('topology', 'lbvh-build', {
      frameId: options.submissionFrameId,
    });
    // Publication waits for this tiny readback. Besides proving that the
    // bottom-up refit converged, it gives candidate traversal the exact tree
    // depth on its very first live frame instead of temporarily selecting the
    // much slower parent-walk path and recording the 4*log2(N) safe wave cap.
    await this.readRefitDepthAsync(
      depthReadback,
      topologyGeneration,
      this.getRefitMaxIterations(this.primCount),
    );

    if (waitForGpuCompletion) {
      await this.device.queue.onSubmittedWorkDone();
    }
  }

  /**
   * Updates leaf and internal bounds while preserving the last Morton topology.
   * Queue ordering makes the refit visible to a subsequently submitted
   * traversal without a CPU/GPU synchronization point.
   */
  async refitAsyncFromGPUBuffers(options: BuildOptions): Promise<void> {
    if (this.disposed) {
      return;
    }
    const {
      positionBuffer,
      indexBuffer,
      primCount,
      positionStride = 3,
      waitForGpuCompletion = false,
      sampleQuality = false,
    } = options;

    const boundedCount = Math.max(0, primCount | 0);
    if (!this.buildBuffers || boundedCount !== this.primCount || boundedCount <= 0) {
      await this.buildAsyncFromGPUBuffers(options);
      return;
    }

    this.positionBuffer = positionBuffer;
    this.indexBuffer = indexBuffer;
    this.positionStride = positionStride;
    if (!this.pipelines) {
      const pipelineReady = this.ensurePipelines();
      if (!this.pipelines) {
        await pipelineReady;
        if (this.disposed || !this.pipelines) {
          return;
        }
      }
    }
    this.ensureStaticBindGroups();

    const encoder = this.device.createCommandEncoder({ label: 'LBVH Refit Encoder' });
    if (sampleQuality) {
      encoder.clearBuffer(this.buildBuffers.qualityState, 0, 4);
      encoder.clearBuffer(this.buildBuffers.qualityState, 3 * 4, 4);
    }
    this.recordRefitPass(encoder, boundedCount);
    if (sampleQuality) {
      this.recordQualityPass(encoder, boundedCount, false);
    }
    const submitRefit = (): void => this.device.queue.submit([encoder.finish()]);
    if (this.submissionLedger) {
      this.submissionLedger.withActualSubmitScope(
        this.device.queue,
        {
          frameId: options.submissionFrameId,
          submissionClass: 'critical',
          label: 'lbvh-standalone-refit',
        },
        submitRefit,
      );
    } else {
      submitRefit();
    }
    this.submissionLedger?.record('critical', 'lbvh-standalone-refit', {
      frameId: options.submissionFrameId,
    });
    if (waitForGpuCompletion) {
      await this.device.queue.onSubmittedWorkDone();
    }
  }

  /**
   * Records a containment-driven sparse refit into a caller-owned encoder.
   * No queue submission or CPU readback occurs here, so the caller can place
   * candidate traversal immediately after it in the same command buffer.
   */
  recordRefitFromGPUBuffers(
    commandEncoder: GPUCommandEncoder,
    options: SparseRefitOptions,
  ): boolean {
    if (this.disposed) {
      return false;
    }
    const boundedCount = Math.max(0, options.primCount | 0);
    if (
      !this.buildBuffers
      || boundedCount !== this.primCount
      || boundedCount <= 0
      || options.dirtyLeafCapacity <= 0
    ) {
      return false;
    }

    this.positionBuffer = options.positionBuffer;
    this.indexBuffer = options.indexBuffer;
    this.positionStride = options.positionStride ?? 3;
    if (!this.pipelines) {
      const pipelineReady = this.ensurePipelines();
      if (!this.pipelines) {
        // This synchronous recording API cannot await cold compilation. Start
        // it for the next call and fail closed instead of recording a partial
        // command graph with missing pipelines.
        void pipelineReady.catch(() => undefined);
        return false;
      }
    }
    this.ensureStaticBindGroups();
    this.ensureSparseBindGroups(options.dirtyLeafBuffer, options.debugBuffer);
    if (!this.buildBuffers || !this.pipelines || !this.sparseBindGroups.prepare) {
      return false;
    }

    this.refitEpoch = (this.refitEpoch + 1) >>> 0;
    if (this.refitEpoch === 0) {
      commandEncoder.clearBuffer(this.buildBuffers.refitMarks);
      this.refitEpoch = 1;
    }

    const workgroupCount = Math.ceil(boundedCount / BUILD_WORKGROUP_SIZE);
    const flags = options.forceFullRefit ? 1 : 0;
    this.device.queue.writeBuffer(
      this.buildBuffers.uniforms,
      UNIFORM_SETUP_OFFSET,
      new Uint32Array([boundedCount, workgroupCount, this.positionStride, 0]),
    );
    this.device.queue.writeBuffer(
      this.buildBuffers.uniforms,
      UNIFORM_LBVH_OFFSET,
      new Uint32Array([boundedCount, 0, 0, 0]),
    );
    this.device.queue.writeBuffer(
      this.buildBuffers.uniforms,
      UNIFORM_SPARSE_OFFSET,
      new Uint32Array([
        boundedCount,
        this.positionStride,
        this.refitEpoch,
        Math.max(1, options.dirtyLeafCapacity | 0),
        flags,
        Math.max(0, options.debugCounterOffset ?? 12),
        0,
        0,
      ]),
    );

    commandEncoder.clearBuffer(this.buildBuffers.activeCount0);
    commandEncoder.clearBuffer(this.buildBuffers.activeCount1);
    commandEncoder.clearBuffer(this.buildBuffers.refitDepth);
    const sampleQuality = Boolean(options.sampleQuality || options.resetQualityBaseline);
    if (sampleQuality) {
      commandEncoder.clearBuffer(this.buildBuffers.qualityState, 0, 4);
      commandEncoder.clearBuffer(this.buildBuffers.qualityState, 3 * 4, 4);
    }

    this.recordSparseRefitPass(commandEncoder, boundedCount);
    if (sampleQuality) {
      this.recordQualityPass(
        commandEncoder,
        boundedCount,
        Boolean(options.resetQualityBaseline),
      );
    }
    this.recordSparseFinalizePass(commandEncoder);
    return true;
  }

  /**
   * Records the dense O(N) refit directly into a caller-owned encoder.
   * The broadphase selects this from delayed GPU telemetry only after a dense
   * sparse fallback has already validated the immutable topology.
   */
  recordFullRefitFromGPUBuffers(
    commandEncoder: GPUCommandEncoder,
    options: BuildOptions,
  ): boolean {
    if (this.disposed) {
      return false;
    }
    const boundedCount = Math.max(0, options.primCount | 0);
    if (!this.buildBuffers || boundedCount !== this.primCount || boundedCount <= 0) {
      return false;
    }
    this.positionBuffer = options.positionBuffer;
    this.indexBuffer = options.indexBuffer;
    this.positionStride = options.positionStride ?? 3;
    if (!this.pipelines) {
      const pipelineReady = this.ensurePipelines();
      if (!this.pipelines) {
        void pipelineReady.catch(() => undefined);
        return false;
      }
    }
    this.ensureStaticBindGroups();
    if (!this.buildBuffers || !this.pipelines) { return false; }

    const sampleQuality = Boolean(options.sampleQuality);
    if (sampleQuality) {
      commandEncoder.clearBuffer(this.buildBuffers.qualityState, 0, 4);
      commandEncoder.clearBuffer(this.buildBuffers.qualityState, 3 * 4, 4);
    }
    this.recordRefitPass(commandEncoder, boundedCount);
    if (sampleQuality) {
      this.recordQualityPass(commandEncoder, boundedCount, false);
    }
    return true;
  }

  dispose(): void {
    this.disposed = true;
    // WebGPU compilation cannot be cancelled. Invalidate the publication
    // token so an already-running async compile can never resurrect pipelines
    // after disposal.
    this.pipelineInitGeneration++;
    this.pipelineInitPromise = null;
    this.topologyGeneration++;
    this.measuredRefitIterations = null;
    if (this.sorter) {
      this.sorter.dispose();
      this.sorter = null;
    }

    if (this.buildBuffers) {
      const buffers = this.buildBuffers as Record<string, GPUBuffer>;
      for (const key of Object.keys(buffers)) {
        buffers[key].destroy();
      }
      this.buildBuffers = null;
    }

    this.pipelines = null;
    this.positionBuffer = null;
    this.indexBuffer = null;
    this.bufferCapacity = 0;
    this.sorterCapacity = 0;
    this.staticBindGroups.setupBounds = null;
    this.staticBindGroups.setupMorton = null;
    this.staticBindGroups.lbvhResetRefitState = null;
    this.staticBindGroups.lbvhRefitLeaves = null;
    this.staticBindGroups.lbvhInitState = null;
    this.staticBindGroups.lbvhBuildTopology = null;
    this.staticBindGroups.lbvhSeedInternal = null;
    this.staticBindGroups.lbvhRefitWave0to1 = null;
    this.staticBindGroups.lbvhRefitWave1to0 = null;
    this.staticBindGroups.lbvhUpdateDispatch0to1 = null;
    this.staticBindGroups.lbvhUpdateDispatch1to0 = null;
    this.staticBindGroups.lbvhFinalize = null;
    this.staticBindGroups.lbvhQualityProbe = null;
    this.staticBindGroups.lbvhQualityFinalizeBaseline = null;
    this.staticBindGroups.lbvhQualityFinalizeSample = null;
    this.staticBindGroupBuffers.buildBuffers = null;
    this.staticBindGroupBuffers.position = null;
    this.staticBindGroupBuffers.index = null;
    for (const key of Object.keys(this.sparseBindGroups) as Array<keyof typeof this.sparseBindGroups>) {
      this.sparseBindGroups[key] = null;
    }
    this.sparseBindGroupBuffers.buildBuffers = null;
    this.sparseBindGroupBuffers.position = null;
    this.sparseBindGroupBuffers.index = null;
    this.sparseBindGroupBuffers.dirty = null;
    this.sparseBindGroupBuffers.debug = null;
    this.refitEpoch = 0;
  }

  private shouldUseOneSweep(): boolean {
    if (this.requestedSorterType !== LBVHSorterType.ONESWEEP) {
      return false;
    }

    const hasSubgroups = this.device.features.has('subgroups' as GPUFeatureName);
    if (!hasSubgroups && !this.warnedNoSubgroups) {
      this.warnedNoSubgroups = true;
      console.warn('GPULBVHBuilder: subgroups unavailable, falling back to builtin radix sort.');
    }
    return hasSubgroups;
  }

  private ensurePipelines(): Promise<void> {
    if (this.pipelines) {
      return Promise.resolve();
    }
    if (this.pipelineInitPromise) {
      return this.pipelineInitPromise;
    }
    if (this.disposed) {
      return Promise.resolve();
    }

    const initGeneration = this.pipelineInitGeneration;
    let shared = SHARED_PIPELINES_BY_DEVICE.get(this.device);
    if (shared?.ready) {
      this.pipelines = shared.ready;
      return Promise.resolve();
    }
    if (!shared) {
      shared = { ready: null, promise: null };
      SHARED_PIPELINES_BY_DEVICE.set(this.device, shared);
    }
    if (!shared.promise) {
      const sharedEntry = shared;
      shared.promise = this.compilePipelinesAsync().then((pipelines) => {
        sharedEntry.ready = pipelines;
        return pipelines;
      }).catch((error: unknown) => {
        if (SHARED_PIPELINES_BY_DEVICE.get(this.device) === sharedEntry) {
          SHARED_PIPELINES_BY_DEVICE.delete(this.device);
        }
        throw error;
      });
    }

    const pending = shared.promise.then((pipelines) => {
      if (!this.disposed && initGeneration === this.pipelineInitGeneration) {
        this.pipelines = pipelines;
      }
    }).finally(() => {
      if (this.pipelineInitPromise === pending) {
        // A failed compile remains retryable; a successful one is retained in
        // this.pipelines. Never leave a rejected promise cached indefinitely.
        this.pipelineInitPromise = null;
      }
    });
    this.pipelineInitPromise = pending;
    return pending;
  }

  private async compilePipelinesAsync(): Promise<Pipelines> {
    const hasSubgroups = this.device.features.has('subgroups' as GPUFeatureName);
    const setupBoundsShader = hasSubgroups ? setupShaders.computeBoundsSubgroup : setupShaders.computeBounds;

    const setupBoundsModule = this.device.createShaderModule({
      label: 'LBVH setupBounds',
      code: setupBoundsShader,
    });
    const setupMortonModule = this.device.createShaderModule({
      label: 'LBVH setupMorton',
      code: setupShaders.computeMorton,
    });
    const lbvhResetRefitStateModule = this.device.createShaderModule({
      label: 'LBVH resetRefitState',
      code: lbvhResetRefitStateShader,
    });
    const lbvhRefitLeavesModule = this.device.createShaderModule({
      label: 'LBVH refitLeaves',
      code: lbvhRefitLeavesShader,
    });

    const radixHistogramModule = this.device.createShaderModule({
      label: 'LBVH radixHistogram',
      code: radixSortShaders.histogram,
    });
    const radixWorkgroupScanModule = this.device.createShaderModule({
      label: 'LBVH radixWorkgroupScan',
      code: radixSortShaders.workgroupScan,
    });
    const radixScanModule = this.device.createShaderModule({
      label: 'LBVH radixScan',
      code: radixSortShaders.scan,
    });
    const radixScatterModule = this.device.createShaderModule({
      label: 'LBVH radixScatter',
      code: radixSortShaders.scatter,
    });

    const lbvhInitStateModule = this.device.createShaderModule({
      label: 'LBVH initState',
      code: lbvhInitStateShader,
    });
    const lbvhBuildTopologyModule = this.device.createShaderModule({
      label: 'LBVH buildTopology',
      code: lbvhBuildTopologyShader,
    });
    const lbvhSeedInternalModule = this.device.createShaderModule({
      label: 'LBVH seedInternal',
      code: lbvhSeedInternalShader,
    });
    const lbvhRefitWaveModule = this.device.createShaderModule({
      label: 'LBVH refitWave',
      code: lbvhRefitWaveShader,
    });
    const lbvhUpdateDispatchModule = this.device.createShaderModule({
      label: 'LBVH updateDispatch',
      code: lbvhUpdateDispatchShader,
    });
    const lbvhFinalizeModule = this.device.createShaderModule({
      label: 'LBVH finalize',
      code: lbvhFinalizeShader,
    });
    const lbvhQualityProbeModule = this.device.createShaderModule({
      label: 'LBVH qualityProbe',
      code: lbvhQualityProbeShader,
    });
    const lbvhQualityFinalizeModule = this.device.createShaderModule({
      label: 'LBVH qualityFinalize',
      code: lbvhQualityFinalizeShader,
    });
    const lbvhSparsePrepareModule = this.device.createShaderModule({
      label: 'LBVH sparsePrepare',
      code: lbvhSparsePrepareShader,
    });
    const lbvhSparseLeavesModule = this.device.createShaderModule({
      label: 'LBVH sparseLeaves',
      code: lbvhSparseLeavesShader,
    });
    const lbvhSparseSeedModule = this.device.createShaderModule({
      label: 'LBVH sparseSeed',
      code: lbvhSparseSeedShader,
    });
    const lbvhSparseWaveModule = this.device.createShaderModule({
      label: 'LBVH sparseWave',
      code: lbvhSparseWaveShader,
    });
    const lbvhSparseValidateModule = this.device.createShaderModule({
      label: 'LBVH sparseValidate',
      code: lbvhSparseValidateShader,
    });
    const lbvhFallbackPrepareModule = this.device.createShaderModule({
      label: 'LBVH fallbackPrepare',
      code: lbvhFallbackPrepareShader,
    });
    const lbvhFallbackResetModule = this.device.createShaderModule({
      label: 'LBVH fallbackReset',
      code: lbvhFallbackResetShader,
    });
    const lbvhFallbackValidatePrepareModule = this.device.createShaderModule({
      label: 'LBVH fallbackValidatePrepare',
      code: lbvhFallbackValidatePrepareShader,
    });
    const lbvhFallbackValidateModule = this.device.createShaderModule({
      label: 'LBVH fallbackValidate',
      code: lbvhFallbackValidateShader,
    });
    const lbvhSparseFinalizeModule = this.device.createShaderModule({
      label: 'LBVH sparseFinalize',
      code: lbvhSparseFinalizeShader,
    });

    const pipelinePromises: {
      [K in keyof Pipelines]: Promise<Pipelines[K]>;
    } = {
      setupBounds: this.device.createComputePipelineAsync({
        label: 'LBVH Setup Bounds',
        layout: 'auto',
        compute: { module: setupBoundsModule, entryPoint: 'computeBounds' },
      }),
      setupMorton: this.device.createComputePipelineAsync({
        label: 'LBVH Setup Morton',
        layout: 'auto',
        compute: { module: setupMortonModule, entryPoint: 'computeMorton' },
      }),
      lbvhResetRefitState: this.device.createComputePipelineAsync({
        label: 'LBVH Reset Refit State',
        layout: 'auto',
        compute: { module: lbvhResetRefitStateModule, entryPoint: 'resetRefitState' },
      }),
      lbvhRefitLeaves: this.device.createComputePipelineAsync({
        label: 'LBVH Refit Leaves',
        layout: 'auto',
        compute: { module: lbvhRefitLeavesModule, entryPoint: 'refitLeaves' },
      }),
      radixHistogram: this.device.createComputePipelineAsync({
        label: 'LBVH Radix Histogram',
        layout: 'auto',
        compute: { module: radixHistogramModule, entryPoint: 'computeHistogram' },
      }),
      radixWorkgroupScan: this.device.createComputePipelineAsync({
        label: 'LBVH Radix WorkgroupScan',
        layout: 'auto',
        compute: { module: radixWorkgroupScanModule, entryPoint: 'workgroupScan' },
      }),
      radixScan: this.device.createComputePipelineAsync({
        label: 'LBVH Radix Scan',
        layout: 'auto',
        compute: { module: radixScanModule, entryPoint: 'prefixScan' },
      }),
      radixScatter: this.device.createComputePipelineAsync({
        label: 'LBVH Radix Scatter',
        layout: 'auto',
        compute: { module: radixScatterModule, entryPoint: 'scatter' },
      }),
      lbvhInitState: this.device.createComputePipelineAsync({
        label: 'LBVH Init State',
        layout: 'auto',
        compute: { module: lbvhInitStateModule, entryPoint: 'initState' },
      }),
      lbvhBuildTopology: this.device.createComputePipelineAsync({
        label: 'LBVH Build Topology',
        layout: 'auto',
        compute: { module: lbvhBuildTopologyModule, entryPoint: 'buildTopology' },
      }),
      lbvhSeedInternal: this.device.createComputePipelineAsync({
        label: 'LBVH Seed Internal',
        layout: 'auto',
        compute: { module: lbvhSeedInternalModule, entryPoint: 'seedInternal' },
      }),
      lbvhRefitWave: this.device.createComputePipelineAsync({
        label: 'LBVH Refit Wave',
        layout: 'auto',
        compute: { module: lbvhRefitWaveModule, entryPoint: 'refitWave' },
      }),
      lbvhUpdateDispatch: this.device.createComputePipelineAsync({
        label: 'LBVH Update Dispatch',
        layout: 'auto',
        compute: { module: lbvhUpdateDispatchModule, entryPoint: 'updateDispatch' },
      }),
      lbvhFinalize: this.device.createComputePipelineAsync({
        label: 'LBVH Finalize',
        layout: 'auto',
        compute: { module: lbvhFinalizeModule, entryPoint: 'finalizeTree' },
      }),
      lbvhQualityProbe: this.device.createComputePipelineAsync({
        label: 'LBVH Quality Probe',
        layout: 'auto',
        compute: { module: lbvhQualityProbeModule, entryPoint: 'probeQuality' },
      }),
      lbvhQualityFinalizeBaseline: this.device.createComputePipelineAsync({
        label: 'LBVH Quality Finalize Baseline',
        layout: 'auto',
        compute: { module: lbvhQualityFinalizeModule, entryPoint: 'finalizeBaseline' },
      }),
      lbvhQualityFinalizeSample: this.device.createComputePipelineAsync({
        label: 'LBVH Quality Finalize Sample',
        layout: 'auto',
        compute: { module: lbvhQualityFinalizeModule, entryPoint: 'finalizeSample' },
      }),
      lbvhSparsePrepare: this.device.createComputePipelineAsync({
        label: 'LBVH Sparse Prepare',
        layout: 'auto',
        compute: { module: lbvhSparsePrepareModule, entryPoint: 'prepareSparseRefit' },
      }),
      lbvhSparseLeaves: this.device.createComputePipelineAsync({
        label: 'LBVH Sparse Leaves',
        layout: 'auto',
        compute: { module: lbvhSparseLeavesModule, entryPoint: 'refitSparseLeaves' },
      }),
      lbvhSparseSeed: this.device.createComputePipelineAsync({
        label: 'LBVH Sparse Seed',
        layout: 'auto',
        compute: { module: lbvhSparseSeedModule, entryPoint: 'seedSparseRefit' },
      }),
      lbvhSparseWave: this.device.createComputePipelineAsync({
        label: 'LBVH Sparse Wave',
        layout: 'auto',
        compute: { module: lbvhSparseWaveModule, entryPoint: 'refitSparseWave' },
      }),
      lbvhSparseValidate: this.device.createComputePipelineAsync({
        label: 'LBVH Sparse Validate',
        layout: 'auto',
        compute: { module: lbvhSparseValidateModule, entryPoint: 'validateSparseRefit' },
      }),
      lbvhFallbackPrepare: this.device.createComputePipelineAsync({
        label: 'LBVH Fallback Prepare',
        layout: 'auto',
        compute: { module: lbvhFallbackPrepareModule, entryPoint: 'prepareFullFallback' },
      }),
      lbvhFallbackReset: this.device.createComputePipelineAsync({
        label: 'LBVH Fallback Reset',
        layout: 'auto',
        compute: { module: lbvhFallbackResetModule, entryPoint: 'resetFullFallback' },
      }),
      lbvhFallbackValidatePrepare: this.device.createComputePipelineAsync({
        label: 'LBVH Fallback Validate Prepare',
        layout: 'auto',
        compute: { module: lbvhFallbackValidatePrepareModule, entryPoint: 'prepareFullValidation' },
      }),
      lbvhFallbackValidate: this.device.createComputePipelineAsync({
        label: 'LBVH Fallback Validate',
        layout: 'auto',
        compute: { module: lbvhFallbackValidateModule, entryPoint: 'validateFullFallback' },
      }),
      lbvhSparseFinalize: this.device.createComputePipelineAsync({
        label: 'LBVH Sparse Finalize',
        layout: 'auto',
        compute: { module: lbvhSparseFinalizeModule, entryPoint: 'finalizeSparseRefit' },
      }),
    };

    // Queue every cold compile before awaiting any one of them. This keeps
    // driver compilation off the animation frame while still allowing the
    // implementation to compile independent kernels concurrently.
    const entries = await Promise.all(
      (Object.keys(pipelinePromises) as Array<keyof Pipelines>).map(async (key) => (
        [key, await pipelinePromises[key]] as const
      )),
    );
    const compiledPipelines = Object.fromEntries(entries) as Pipelines;
    return compiledPipelines;
  }

  private async ensureSorter(primCount: number): Promise<void> {
    if (!this.shouldUseOneSweep()) {
      return;
    }

    if (!this.sorter) {
      // OneSweepSorter is recovered JavaScript, so TypeScript infers the
      // default-null parameter too narrowly even though runtime accepts the
      // same ledger used by the builtin sorter path.
      this.sorter = new OneSweepSorter(this.device, this.submissionLedger as any);
    }

    const targetCapacity = Math.max(primCount, 1);
    if (this.sorterInitPromise) {
      await this.sorterInitPromise;
    }
    if (this.sorterCapacity < targetCapacity) {
      this.sorterInitPromise = this.sorter.init(targetCapacity).then(() => {
        this.sorterCapacity = targetCapacity;
      }).finally(() => {
        this.sorterInitPromise = null;
      });
      await this.sorterInitPromise;
    }
  }

  private allocateBuffers(primCount: number): void {
    if (primCount <= this.bufferCapacity && this.buildBuffers) {
      return;
    }

    const newCapacity = Math.max(1024, this.nextPowerOf2(Math.max(1, primCount)));
    const workgroupCount = Math.max(1, Math.ceil(newCapacity / RADIX_WORKGROUP_SIZE));
    const maxNodes = Math.max(2, newCapacity * 2);

    if (this.buildBuffers) {
      const old = this.buildBuffers as Record<string, GPUBuffer>;
      for (const key of Object.keys(old)) {
        old[key].destroy();
      }
      this.staticBindGroups.setupBounds = null;
      this.staticBindGroups.setupMorton = null;
      this.staticBindGroups.lbvhResetRefitState = null;
      this.staticBindGroups.lbvhRefitLeaves = null;
      this.staticBindGroups.lbvhInitState = null;
      this.staticBindGroups.lbvhBuildTopology = null;
      this.staticBindGroups.lbvhSeedInternal = null;
      this.staticBindGroups.lbvhRefitWave0to1 = null;
      this.staticBindGroups.lbvhRefitWave1to0 = null;
      this.staticBindGroups.lbvhUpdateDispatch0to1 = null;
      this.staticBindGroups.lbvhUpdateDispatch1to0 = null;
      this.staticBindGroups.lbvhFinalize = null;
      this.staticBindGroups.lbvhQualityProbe = null;
      this.staticBindGroups.lbvhQualityFinalizeBaseline = null;
      this.staticBindGroups.lbvhQualityFinalizeSample = null;
      this.staticBindGroupBuffers.buildBuffers = null;
      this.staticBindGroupBuffers.position = null;
      this.staticBindGroupBuffers.index = null;
      for (const key of Object.keys(this.sparseBindGroups) as Array<keyof typeof this.sparseBindGroups>) {
        this.sparseBindGroups[key] = null;
      }
      this.sparseBindGroupBuffers.buildBuffers = null;
      this.sparseBindGroupBuffers.position = null;
      this.sparseBindGroupBuffers.index = null;
      this.sparseBindGroupBuffers.dirty = null;
      this.sparseBindGroupBuffers.debug = null;
      this.refitEpoch = 0;
    }

    this.buildBuffers = {
      sceneBounds: this.device.createBuffer({
        label: 'LBVH Scene Bounds',
        size: 24,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      }),
      mortonCodes: this.device.createBuffer({
        label: 'LBVH Morton',
        size: newCapacity * 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      }),
      mortonCodesAlt: this.device.createBuffer({
        label: 'LBVH Morton Alt',
        size: newCapacity * 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      }),
      clusterIdx: this.device.createBuffer({
        label: 'LBVH ClusterIdx',
        size: newCapacity * 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      }),
      clusterIdxAlt: this.device.createBuffer({
        label: 'LBVH ClusterIdx Alt',
        size: newCapacity * 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      }),
      hplocState: this.device.createBuffer({
        label: 'LBVH HplocState Scratch',
        size: newCapacity * 4 * 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      }),
      activeList: this.device.createBuffer({
        label: 'LBVH ActiveList Scratch',
        size: newCapacity * 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      }),
      parentIdx: this.device.createBuffer({
        label: 'LBVH ParentIdx',
        size: maxNodes * 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      }),
      refitVisitCount: this.device.createBuffer({
        label: 'LBVH VisitCount',
        size: maxNodes * 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      }),
      activeCount0: this.device.createBuffer({
        label: 'LBVH ActiveCount0',
        size: 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      }),
      activeCount1: this.device.createBuffer({
        label: 'LBVH ActiveCount1',
        size: 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      }),
      refitDepth: this.device.createBuffer({
        label: 'LBVH Refit Depth',
        size: 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC | GPUBufferUsage.COPY_DST,
      }),
      qualityState: this.device.createBuffer({
        label: 'LBVH Quality State',
        size: 6 * 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC | GPUBufferUsage.COPY_DST,
      }),
      refitMarks: this.device.createBuffer({
        label: 'LBVH Sparse Refit Marks',
        size: maxNodes * 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      }),
      refitState: this.device.createBuffer({
        label: 'LBVH Sparse Refit State',
        size: 12 * 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      }),
      indirectDispatch: this.device.createBuffer({
        label: 'LBVH IndirectDispatch',
        size: 3 * 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.INDIRECT | GPUBufferUsage.COPY_DST,
      }),
      fallbackGateDispatch: this.device.createBuffer({
        label: 'LBVH Full Fallback Gate Dispatch',
        size: 3 * 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.INDIRECT | GPUBufferUsage.COPY_DST,
      }),
      bvh2Nodes: this.device.createBuffer({
        label: 'LBVH Nodes',
        size: maxNodes * 32,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      }),
      nodeCounter: this.device.createBuffer({
        label: 'LBVH NodeCounter',
        size: 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      }),
      groupCounts: this.device.createBuffer({
        label: 'LBVH GroupCounts',
        size: workgroupCount * RADIX_SIZE * 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      }),
      groupPrefix: this.device.createBuffer({
        label: 'LBVH GroupPrefix',
        size: workgroupCount * RADIX_SIZE * 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      }),
      globalDigitCount: this.device.createBuffer({
        label: 'LBVH GlobalDigitCount',
        size: RADIX_SIZE * 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      }),
      digitOffsets: this.device.createBuffer({
        label: 'LBVH DigitOffsets',
        size: RADIX_SIZE * 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      }),
      uniforms: this.device.createBuffer({
        label: 'LBVH Uniforms',
        size: UNIFORM_ALIGN * UNIFORM_SLOT_COUNT,
        usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
      }),
    };

    this.bufferCapacity = newCapacity;
  }

  private ensureStaticBindGroups(): void {
    if (!this.buildBuffers || !this.pipelines || !this.positionBuffer || !this.indexBuffer) {
      return;
    }

    const needsRebuild =
      this.staticBindGroupBuffers.buildBuffers !== this.buildBuffers
      || this.staticBindGroupBuffers.position !== this.positionBuffer
      || this.staticBindGroupBuffers.index !== this.indexBuffer
      || !this.staticBindGroups.setupBounds
      || !this.staticBindGroups.setupMorton
      || !this.staticBindGroups.lbvhResetRefitState
      || !this.staticBindGroups.lbvhRefitLeaves
      || !this.staticBindGroups.lbvhInitState
      || !this.staticBindGroups.lbvhBuildTopology
      || !this.staticBindGroups.lbvhSeedInternal
      || !this.staticBindGroups.lbvhRefitWave0to1
      || !this.staticBindGroups.lbvhRefitWave1to0
      || !this.staticBindGroups.lbvhUpdateDispatch0to1
      || !this.staticBindGroups.lbvhUpdateDispatch1to0
      || !this.staticBindGroups.lbvhFinalize
      || !this.staticBindGroups.lbvhQualityProbe
      || !this.staticBindGroups.lbvhQualityFinalizeBaseline
      || !this.staticBindGroups.lbvhQualityFinalizeSample;

    if (!needsRebuild) {
      return;
    }

    const b = this.buildBuffers;
    const p = this.pipelines;
    this.staticBindGroups.setupBounds = this.device.createBindGroup({
      layout: p.setupBounds.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: b.uniforms, offset: UNIFORM_SETUP_OFFSET, size: UNIFORM_BYTES } },
        { binding: 1, resource: { buffer: this.positionBuffer } },
        { binding: 2, resource: { buffer: this.indexBuffer } },
        { binding: 3, resource: { buffer: b.bvh2Nodes } },
        { binding: 4, resource: { buffer: b.clusterIdx } },
        { binding: 5, resource: { buffer: b.sceneBounds } },
        { binding: 6, resource: { buffer: b.parentIdx } },
        { binding: 7, resource: { buffer: b.hplocState } },
        { binding: 8, resource: { buffer: b.activeList } },
      ],
    });
    this.staticBindGroups.setupMorton = this.device.createBindGroup({
      layout: p.setupMorton.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: b.uniforms, offset: UNIFORM_MORTON_OFFSET, size: UNIFORM_BYTES } },
        { binding: 1, resource: { buffer: b.bvh2Nodes } },
        { binding: 2, resource: { buffer: b.sceneBounds } },
        { binding: 3, resource: { buffer: b.mortonCodes } },
      ],
    });
    this.staticBindGroups.lbvhResetRefitState = this.device.createBindGroup({
      layout: p.lbvhResetRefitState.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: b.uniforms, offset: UNIFORM_LBVH_OFFSET, size: UNIFORM_BYTES } },
        { binding: 1, resource: { buffer: b.refitVisitCount } },
      ],
    });
    this.staticBindGroups.lbvhRefitLeaves = this.device.createBindGroup({
      layout: p.lbvhRefitLeaves.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: b.uniforms, offset: UNIFORM_SETUP_OFFSET, size: UNIFORM_BYTES } },
        { binding: 1, resource: { buffer: this.positionBuffer } },
        { binding: 2, resource: { buffer: this.indexBuffer } },
        { binding: 3, resource: { buffer: b.bvh2Nodes } },
      ],
    });
    this.staticBindGroups.lbvhInitState = this.device.createBindGroup({
      layout: p.lbvhInitState.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: b.uniforms, offset: UNIFORM_LBVH_OFFSET, size: UNIFORM_BYTES } },
        { binding: 1, resource: { buffer: b.parentIdx } },
        { binding: 2, resource: { buffer: b.refitVisitCount } },
      ],
    });
    this.staticBindGroups.lbvhBuildTopology = this.device.createBindGroup({
      layout: p.lbvhBuildTopology.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: b.uniforms, offset: UNIFORM_LBVH_OFFSET, size: UNIFORM_BYTES } },
        { binding: 1, resource: { buffer: b.mortonCodes } },
        { binding: 2, resource: { buffer: b.clusterIdx } },
        { binding: 3, resource: { buffer: b.bvh2Nodes } },
        { binding: 4, resource: { buffer: b.parentIdx } },
      ],
    });
    this.staticBindGroups.lbvhSeedInternal = this.device.createBindGroup({
      layout: p.lbvhSeedInternal.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: b.uniforms, offset: UNIFORM_LBVH_OFFSET, size: UNIFORM_BYTES } },
        { binding: 1, resource: { buffer: b.parentIdx } },
        { binding: 2, resource: { buffer: b.refitVisitCount } },
        { binding: 3, resource: { buffer: b.activeList } },
        { binding: 4, resource: { buffer: b.activeCount0 } },
      ],
    });
    this.staticBindGroups.lbvhRefitWave0to1 = this.device.createBindGroup({
      layout: p.lbvhRefitWave.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: b.uniforms, offset: UNIFORM_LBVH_OFFSET, size: UNIFORM_BYTES } },
        { binding: 1, resource: { buffer: b.bvh2Nodes } },
        { binding: 2, resource: { buffer: b.parentIdx } },
        { binding: 3, resource: { buffer: b.refitVisitCount } },
        { binding: 4, resource: { buffer: b.activeList } },
        { binding: 5, resource: { buffer: b.clusterIdxAlt } },
        { binding: 6, resource: { buffer: b.activeCount0 } },
        { binding: 7, resource: { buffer: b.activeCount1 } },
      ],
    });
    this.staticBindGroups.lbvhRefitWave1to0 = this.device.createBindGroup({
      layout: p.lbvhRefitWave.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: b.uniforms, offset: UNIFORM_LBVH_OFFSET, size: UNIFORM_BYTES } },
        { binding: 1, resource: { buffer: b.bvh2Nodes } },
        { binding: 2, resource: { buffer: b.parentIdx } },
        { binding: 3, resource: { buffer: b.refitVisitCount } },
        { binding: 4, resource: { buffer: b.clusterIdxAlt } },
        { binding: 5, resource: { buffer: b.activeList } },
        { binding: 6, resource: { buffer: b.activeCount1 } },
        { binding: 7, resource: { buffer: b.activeCount0 } },
      ],
    });
    this.staticBindGroups.lbvhUpdateDispatch0to1 = this.device.createBindGroup({
      layout: p.lbvhUpdateDispatch.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: b.activeCount0 } },
        { binding: 1, resource: { buffer: b.indirectDispatch } },
        { binding: 2, resource: { buffer: b.activeCount1 } },
        { binding: 3, resource: { buffer: b.refitDepth } },
      ],
    });
    this.staticBindGroups.lbvhUpdateDispatch1to0 = this.device.createBindGroup({
      layout: p.lbvhUpdateDispatch.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: b.activeCount1 } },
        { binding: 1, resource: { buffer: b.indirectDispatch } },
        { binding: 2, resource: { buffer: b.activeCount0 } },
        { binding: 3, resource: { buffer: b.refitDepth } },
      ],
    });
    this.staticBindGroups.lbvhFinalize = this.device.createBindGroup({
      layout: p.lbvhFinalize.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: b.uniforms, offset: UNIFORM_LBVH_OFFSET, size: UNIFORM_BYTES } },
        { binding: 1, resource: { buffer: b.clusterIdx } },
        { binding: 2, resource: { buffer: b.nodeCounter } },
      ],
    });
    this.staticBindGroups.lbvhQualityProbe = this.device.createBindGroup({
      layout: p.lbvhQualityProbe.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: b.uniforms, offset: UNIFORM_LBVH_OFFSET, size: UNIFORM_BYTES } },
        { binding: 1, resource: { buffer: b.bvh2Nodes } },
        { binding: 2, resource: { buffer: b.qualityState } },
      ],
    });
    this.staticBindGroups.lbvhQualityFinalizeBaseline = this.device.createBindGroup({
      layout: p.lbvhQualityFinalizeBaseline.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: b.qualityState } },
      ],
    });
    this.staticBindGroups.lbvhQualityFinalizeSample = this.device.createBindGroup({
      layout: p.lbvhQualityFinalizeSample.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: b.qualityState } },
      ],
    });

    this.staticBindGroupBuffers.buildBuffers = this.buildBuffers;
    this.staticBindGroupBuffers.position = this.positionBuffer;
    this.staticBindGroupBuffers.index = this.indexBuffer;
  }

  private ensureSparseBindGroups(dirtyBuffer: GPUBuffer, debugBuffer: GPUBuffer): void {
    if (!this.buildBuffers || !this.pipelines || !this.positionBuffer || !this.indexBuffer) {
      return;
    }
    const cached = this.sparseBindGroupBuffers;
    const groups = this.sparseBindGroups;
    const needsRebuild = cached.buildBuffers !== this.buildBuffers
      || cached.position !== this.positionBuffer
      || cached.index !== this.indexBuffer
      || cached.dirty !== dirtyBuffer
      || cached.debug !== debugBuffer
      || !groups.prepare || !groups.leaves || !groups.seed
      || !groups.wave0to1 || !groups.wave1to0 || !groups.validate
      || !groups.fallbackPrepare || !groups.fallbackReset
      || !groups.fallbackValidatePrepare || !groups.fallbackValidate
      || !groups.finalize;
    if (!needsRebuild) { return; }

    const b = this.buildBuffers;
    const p = this.pipelines;
    const sparseUniform = { buffer: b.uniforms, offset: UNIFORM_SPARSE_OFFSET, size: SPARSE_UNIFORM_BYTES };
    groups.prepare = this.device.createBindGroup({
      layout: p.lbvhSparsePrepare.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: sparseUniform },
        { binding: 1, resource: { buffer: dirtyBuffer } },
        { binding: 2, resource: { buffer: b.indirectDispatch } },
        { binding: 3, resource: { buffer: b.refitState } },
        { binding: 4, resource: { buffer: b.fallbackGateDispatch } },
      ],
    });
    groups.leaves = this.device.createBindGroup({
      layout: p.lbvhSparseLeaves.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: sparseUniform },
        { binding: 1, resource: { buffer: this.positionBuffer } },
        { binding: 2, resource: { buffer: this.indexBuffer } },
        { binding: 3, resource: { buffer: b.bvh2Nodes } },
        { binding: 4, resource: { buffer: b.parentIdx } },
        { binding: 5, resource: { buffer: b.refitVisitCount } },
        { binding: 6, resource: { buffer: b.refitMarks } },
        { binding: 7, resource: { buffer: dirtyBuffer } },
        { binding: 8, resource: { buffer: b.refitState } },
      ],
    });
    groups.seed = this.device.createBindGroup({
      layout: p.lbvhSparseSeed.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: sparseUniform },
        { binding: 1, resource: { buffer: b.bvh2Nodes } },
        { binding: 2, resource: { buffer: b.parentIdx } },
        { binding: 3, resource: { buffer: b.refitVisitCount } },
        { binding: 4, resource: { buffer: b.refitMarks } },
        { binding: 5, resource: { buffer: dirtyBuffer } },
        { binding: 6, resource: { buffer: b.activeList } },
        { binding: 7, resource: { buffer: b.activeCount0 } },
        { binding: 8, resource: { buffer: b.refitState } },
      ],
    });
    groups.wave0to1 = this.device.createBindGroup({
      layout: p.lbvhSparseWave.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: sparseUniform },
        { binding: 1, resource: { buffer: b.bvh2Nodes } },
        { binding: 2, resource: { buffer: b.parentIdx } },
        { binding: 3, resource: { buffer: b.refitVisitCount } },
        { binding: 4, resource: { buffer: b.refitMarks } },
        { binding: 5, resource: { buffer: b.activeList } },
        { binding: 6, resource: { buffer: b.clusterIdxAlt } },
        { binding: 7, resource: { buffer: b.activeCount0 } },
        { binding: 8, resource: { buffer: b.activeCount1 } },
        { binding: 9, resource: { buffer: b.refitState } },
      ],
    });
    groups.wave1to0 = this.device.createBindGroup({
      layout: p.lbvhSparseWave.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: sparseUniform },
        { binding: 1, resource: { buffer: b.bvh2Nodes } },
        { binding: 2, resource: { buffer: b.parentIdx } },
        { binding: 3, resource: { buffer: b.refitVisitCount } },
        { binding: 4, resource: { buffer: b.refitMarks } },
        { binding: 5, resource: { buffer: b.clusterIdxAlt } },
        { binding: 6, resource: { buffer: b.activeList } },
        { binding: 7, resource: { buffer: b.activeCount1 } },
        { binding: 8, resource: { buffer: b.activeCount0 } },
        { binding: 9, resource: { buffer: b.refitState } },
      ],
    });
    groups.validate = this.device.createBindGroup({
      layout: p.lbvhSparseValidate.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: sparseUniform },
        { binding: 1, resource: { buffer: b.refitState } },
      ],
    });
    groups.fallbackPrepare = this.device.createBindGroup({
      layout: p.lbvhFallbackPrepare.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: sparseUniform },
        { binding: 1, resource: { buffer: b.refitState } },
        { binding: 2, resource: { buffer: b.indirectDispatch } },
        { binding: 3, resource: { buffer: b.activeCount0 } },
        { binding: 4, resource: { buffer: b.activeCount1 } },
        { binding: 5, resource: { buffer: b.fallbackGateDispatch } },
      ],
    });
    groups.fallbackReset = this.device.createBindGroup({
      layout: p.lbvhFallbackReset.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: sparseUniform },
        { binding: 1, resource: { buffer: b.refitVisitCount } },
      ],
    });
    groups.fallbackValidatePrepare = this.device.createBindGroup({
      layout: p.lbvhFallbackValidatePrepare.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: sparseUniform },
        { binding: 1, resource: { buffer: b.refitState } },
        { binding: 2, resource: { buffer: b.indirectDispatch } },
      ],
    });
    groups.fallbackValidate = this.device.createBindGroup({
      layout: p.lbvhFallbackValidate.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: sparseUniform },
        { binding: 1, resource: { buffer: b.bvh2Nodes } },
        { binding: 2, resource: { buffer: b.parentIdx } },
        { binding: 3, resource: { buffer: b.refitState } },
      ],
    });
    groups.finalize = this.device.createBindGroup({
      layout: p.lbvhSparseFinalize.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: sparseUniform },
        { binding: 1, resource: { buffer: dirtyBuffer } },
        { binding: 2, resource: { buffer: b.refitState } },
        { binding: 3, resource: { buffer: debugBuffer } },
      ],
    });

    cached.buildBuffers = b;
    cached.position = this.positionBuffer;
    cached.index = this.indexBuffer;
    cached.dirty = dirtyBuffer;
    cached.debug = debugBuffer;
  }

  private initBuildState(): void {
    if (!this.buildBuffers) {
      return;
    }

    // +Infinity for mins, -Infinity for maxs in sortable-u32 space
    const boundsInitU32 = new Uint32Array([
      0xFF800000, 0xFF800000, 0xFF800000,
      0x007FFFFF, 0x007FFFFF, 0x007FFFFF,
    ]);
    this.device.queue.writeBuffer(this.buildBuffers.sceneBounds, 0, boundsInitU32);
    this.device.queue.writeBuffer(this.buildBuffers.nodeCounter, 0, new Uint32Array([this.primCount]));
  }

  private recordSetupPass(commandEncoder: GPUCommandEncoder, primCount: number): void {
    if (!this.buildBuffers || !this.pipelines || !this.staticBindGroups.setupBounds || !this.staticBindGroups.setupMorton) {
      return;
    }

    const workgroupCount = Math.ceil(primCount / BUILD_WORKGROUP_SIZE);
    this.device.queue.writeBuffer(this.buildBuffers.uniforms, UNIFORM_SETUP_OFFSET, new Uint32Array([
      primCount,
      workgroupCount,
      this.positionStride,
      0,
    ]));
    this.device.queue.writeBuffer(this.buildBuffers.uniforms, UNIFORM_MORTON_OFFSET, new Uint32Array([
      primCount,
      workgroupCount,
      0,
      0,
    ]));

    const pass = commandEncoder.beginComputePass({ label: 'LBVH Setup Pass' });
    pass.setPipeline(this.pipelines.setupBounds);
    pass.setBindGroup(0, this.staticBindGroups.setupBounds);
    pass.dispatchWorkgroups(workgroupCount);
    pass.setPipeline(this.pipelines.setupMorton);
    pass.setBindGroup(0, this.staticBindGroups.setupMorton);
    pass.dispatchWorkgroups(workgroupCount);
    pass.end();
  }

  private recordOneSweepSort(commandEncoder: GPUCommandEncoder, primCount: number): void {
    if (!this.buildBuffers || !this.sorter) {
      return;
    }

    this.sorter.sort({
      commandEncoder,
      keysIn: this.buildBuffers.mortonCodes,
      keysOut: this.buildBuffers.mortonCodesAlt,
      valsIn: this.buildBuffers.clusterIdx,
      valsOut: this.buildBuffers.clusterIdxAlt,
      count: primCount,
    });
  }

  private recordBuiltinRadixSort(commandEncoder: GPUCommandEncoder, primCount: number): void {
    if (!this.buildBuffers || !this.pipelines) {
      return;
    }

    const workgroupCount = Math.ceil(primCount / RADIX_WORKGROUP_SIZE);
    commandEncoder.clearBuffer(this.buildBuffers.groupCounts);
    commandEncoder.clearBuffer(this.buildBuffers.groupPrefix);
    commandEncoder.clearBuffer(this.buildBuffers.digitOffsets);

    let keysIn = this.buildBuffers.mortonCodes;
    let keysOut = this.buildBuffers.mortonCodesAlt;
    let valsIn = this.buildBuffers.clusterIdx;
    let valsOut = this.buildBuffers.clusterIdxAlt;

    for (let passId = 0; passId < RADIX_PASSES; passId++) {
      const passUniformOffset = UNIFORM_RADIX_BASE_OFFSET + (passId * UNIFORM_ALIGN);
      this.device.queue.writeBuffer(
        this.buildBuffers.uniforms,
        passUniformOffset,
        new Uint32Array([primCount, passId * 8, workgroupCount, 0]),
      );

      commandEncoder.clearBuffer(this.buildBuffers.globalDigitCount);

      {
        const pass = commandEncoder.beginComputePass({ label: `LBVH Radix Histogram ${passId}` });
        const bindGroup = this.device.createBindGroup({
          layout: this.pipelines.radixHistogram.getBindGroupLayout(0),
          entries: [
            { binding: 0, resource: { buffer: this.buildBuffers.uniforms, offset: passUniformOffset, size: UNIFORM_BYTES } },
            { binding: 1, resource: { buffer: keysIn } },
            { binding: 2, resource: { buffer: this.buildBuffers.groupCounts } },
            { binding: 3, resource: { buffer: this.buildBuffers.globalDigitCount } },
          ],
        });
        pass.setPipeline(this.pipelines.radixHistogram);
        pass.setBindGroup(0, bindGroup);
        pass.dispatchWorkgroups(workgroupCount);
        pass.end();
      }

      {
        const pass = commandEncoder.beginComputePass({ label: `LBVH Radix WorkgroupScan ${passId}` });
        const bindGroup = this.device.createBindGroup({
          layout: this.pipelines.radixWorkgroupScan.getBindGroupLayout(0),
          entries: [
            { binding: 0, resource: { buffer: this.buildBuffers.uniforms, offset: passUniformOffset, size: UNIFORM_BYTES } },
            { binding: 1, resource: { buffer: this.buildBuffers.groupCounts } },
            { binding: 2, resource: { buffer: this.buildBuffers.groupPrefix } },
          ],
        });
        pass.setPipeline(this.pipelines.radixWorkgroupScan);
        pass.setBindGroup(0, bindGroup);
        pass.dispatchWorkgroups(1);
        pass.end();
      }

      {
        const pass = commandEncoder.beginComputePass({ label: `LBVH Radix Scan ${passId}` });
        const bindGroup = this.device.createBindGroup({
          layout: this.pipelines.radixScan.getBindGroupLayout(0),
          entries: [
            { binding: 1, resource: { buffer: this.buildBuffers.globalDigitCount } },
            { binding: 2, resource: { buffer: this.buildBuffers.digitOffsets } },
          ],
        });
        pass.setPipeline(this.pipelines.radixScan);
        pass.setBindGroup(0, bindGroup);
        pass.dispatchWorkgroups(1);
        pass.end();
      }

      {
        const pass = commandEncoder.beginComputePass({ label: `LBVH Radix Scatter ${passId}` });
        const bindGroup = this.device.createBindGroup({
          layout: this.pipelines.radixScatter.getBindGroupLayout(0),
          entries: [
            { binding: 0, resource: { buffer: this.buildBuffers.uniforms, offset: passUniformOffset, size: UNIFORM_BYTES } },
            { binding: 1, resource: { buffer: keysIn } },
            { binding: 2, resource: { buffer: keysOut } },
            { binding: 3, resource: { buffer: valsIn } },
            { binding: 4, resource: { buffer: valsOut } },
            { binding: 5, resource: { buffer: this.buildBuffers.groupPrefix } },
            { binding: 6, resource: { buffer: this.buildBuffers.digitOffsets } },
          ],
        });
        pass.setPipeline(this.pipelines.radixScatter);
        pass.setBindGroup(0, bindGroup);
        pass.dispatchWorkgroups(workgroupCount);
        pass.end();
      }
      [keysIn, keysOut] = [keysOut, keysIn];
      [valsIn, valsOut] = [valsOut, valsIn];
    }
  }

  private recordRefitPass(commandEncoder: GPUCommandEncoder, primCount: number): void {
    if (
      !this.buildBuffers
      || !this.pipelines
      || !this.staticBindGroups.lbvhResetRefitState
      || !this.staticBindGroups.lbvhRefitLeaves
      || !this.staticBindGroups.lbvhSeedInternal
      || !this.staticBindGroups.lbvhRefitWave0to1
      || !this.staticBindGroups.lbvhRefitWave1to0
      || !this.staticBindGroups.lbvhUpdateDispatch0to1
      || !this.staticBindGroups.lbvhUpdateDispatch1to0
    ) {
      return;
    }

    const workgroupCount = Math.ceil(primCount / BUILD_WORKGROUP_SIZE);
    this.device.queue.writeBuffer(
      this.buildBuffers.uniforms,
      UNIFORM_SETUP_OFFSET,
      new Uint32Array([primCount, workgroupCount, this.positionStride, 0]),
    );
    this.device.queue.writeBuffer(
      this.buildBuffers.uniforms,
      UNIFORM_LBVH_OFFSET,
      new Uint32Array([primCount, 0, 0, 0]),
    );
    commandEncoder.clearBuffer(this.buildBuffers.activeCount0);
    commandEncoder.clearBuffer(this.buildBuffers.activeCount1);
    commandEncoder.clearBuffer(this.buildBuffers.refitDepth);

    const pass = commandEncoder.beginComputePass({ label: 'LBVH Refit Pass' });
    pass.setPipeline(this.pipelines.lbvhResetRefitState);
    pass.setBindGroup(0, this.staticBindGroups.lbvhResetRefitState);
    pass.dispatchWorkgroups(Math.ceil((primCount * 2) / BUILD_WORKGROUP_SIZE));

    pass.setPipeline(this.pipelines.lbvhRefitLeaves);
    pass.setBindGroup(0, this.staticBindGroups.lbvhRefitLeaves);
    pass.dispatchWorkgroups(workgroupCount);

    if (primCount > 1) {
      pass.setPipeline(this.pipelines.lbvhSeedInternal);
      pass.setBindGroup(0, this.staticBindGroups.lbvhSeedInternal);
      pass.dispatchWorkgroups(workgroupCount);

      const maxIterations = this.getRefitIterationLimit(primCount);
      for (let iter = 0; iter < maxIterations; iter++) {
        const even = (iter & 1) === 0;
        pass.setPipeline(this.pipelines.lbvhUpdateDispatch);
        pass.setBindGroup(
          0,
          even ? this.staticBindGroups.lbvhUpdateDispatch0to1 : this.staticBindGroups.lbvhUpdateDispatch1to0,
        );
        pass.dispatchWorkgroups(1);
        pass.setPipeline(this.pipelines.lbvhRefitWave);
        pass.setBindGroup(
          0,
          even ? this.staticBindGroups.lbvhRefitWave0to1 : this.staticBindGroups.lbvhRefitWave1to0,
        );
        pass.dispatchWorkgroupsIndirect(this.buildBuffers.indirectDispatch, 0);
      }
    }
    pass.end();
  }

  private recordSparseRefitPass(commandEncoder: GPUCommandEncoder, primCount: number): void {
    if (
      !this.buildBuffers || !this.pipelines
      || !this.sparseBindGroups.prepare || !this.sparseBindGroups.leaves
      || !this.sparseBindGroups.seed || !this.sparseBindGroups.wave0to1
      || !this.sparseBindGroups.wave1to0 || !this.sparseBindGroups.validate
      || !this.sparseBindGroups.fallbackPrepare || !this.sparseBindGroups.fallbackReset
      || !this.sparseBindGroups.fallbackValidatePrepare || !this.sparseBindGroups.fallbackValidate
      || !this.staticBindGroups.lbvhRefitLeaves || !this.staticBindGroups.lbvhSeedInternal
      || !this.staticBindGroups.lbvhRefitWave0to1 || !this.staticBindGroups.lbvhRefitWave1to0
      || !this.staticBindGroups.lbvhUpdateDispatch0to1
      || !this.staticBindGroups.lbvhUpdateDispatch1to0
    ) {
      return;
    }

    const pass = commandEncoder.beginComputePass({ label: 'LBVH Sparse Refit Pass' });
    pass.setPipeline(this.pipelines.lbvhSparsePrepare);
    pass.setBindGroup(0, this.sparseBindGroups.prepare);
    pass.dispatchWorkgroups(1);

    pass.setPipeline(this.pipelines.lbvhSparseLeaves);
    pass.setBindGroup(0, this.sparseBindGroups.leaves);
    pass.dispatchWorkgroupsIndirect(this.buildBuffers.indirectDispatch, 0);
    pass.setPipeline(this.pipelines.lbvhSparseSeed);
    pass.setBindGroup(0, this.sparseBindGroups.seed);
    pass.dispatchWorkgroupsIndirect(this.buildBuffers.indirectDispatch, 0);

    const sparseIterations = this.getRefitIterationLimit(primCount);
    for (let iter = 0; iter < sparseIterations; iter++) {
      const even = (iter & 1) === 0;
      pass.setPipeline(this.pipelines.lbvhUpdateDispatch);
      pass.setBindGroup(
        0,
        even ? this.staticBindGroups.lbvhUpdateDispatch0to1 : this.staticBindGroups.lbvhUpdateDispatch1to0,
      );
      pass.dispatchWorkgroupsIndirect(this.buildBuffers.fallbackGateDispatch, 0);
      pass.setPipeline(this.pipelines.lbvhSparseWave);
      pass.setBindGroup(0, even ? this.sparseBindGroups.wave0to1 : this.sparseBindGroups.wave1to0);
      pass.dispatchWorkgroupsIndirect(this.buildBuffers.indirectDispatch, 0);
    }

    pass.setPipeline(this.pipelines.lbvhSparseValidate);
    pass.setBindGroup(0, this.sparseBindGroups.validate);
    pass.dispatchWorkgroups(1);
    pass.setPipeline(this.pipelines.lbvhFallbackPrepare);
    pass.setBindGroup(0, this.sparseBindGroups.fallbackPrepare);
    pass.dispatchWorkgroups(1);
    pass.setPipeline(this.pipelines.lbvhFallbackReset);
    pass.setBindGroup(0, this.sparseBindGroups.fallbackReset);
    pass.dispatchWorkgroupsIndirect(this.buildBuffers.indirectDispatch, 0);

    pass.setPipeline(this.pipelines.lbvhRefitLeaves);
    pass.setBindGroup(0, this.staticBindGroups.lbvhRefitLeaves);
    pass.dispatchWorkgroupsIndirect(this.buildBuffers.indirectDispatch, 0);
    if (primCount > 1) {
      pass.setPipeline(this.pipelines.lbvhSeedInternal);
      pass.setBindGroup(0, this.staticBindGroups.lbvhSeedInternal);
      pass.dispatchWorkgroupsIndirect(this.buildBuffers.indirectDispatch, 0);

      const fullIterations = this.getRefitIterationLimit(primCount);
      for (let iter = 0; iter < fullIterations; iter++) {
        const even = (iter & 1) === 0;
        pass.setPipeline(this.pipelines.lbvhUpdateDispatch);
        pass.setBindGroup(
          0,
          even ? this.staticBindGroups.lbvhUpdateDispatch0to1 : this.staticBindGroups.lbvhUpdateDispatch1to0,
        );
        // The GPU writes x=0 when no fallback is needed.  Thus the recorded
        // safety path contributes no full-refit compute work on static/sparse
        // frames, while still remaining available in this same command buffer.
        pass.dispatchWorkgroupsIndirect(this.buildBuffers.fallbackGateDispatch, 0);
        pass.setPipeline(this.pipelines.lbvhRefitWave);
        pass.setBindGroup(
          0,
          even ? this.staticBindGroups.lbvhRefitWave0to1 : this.staticBindGroups.lbvhRefitWave1to0,
        );
        pass.dispatchWorkgroupsIndirect(this.buildBuffers.indirectDispatch, 0);
      }
    }
    pass.setPipeline(this.pipelines.lbvhFallbackValidatePrepare);
    pass.setBindGroup(0, this.sparseBindGroups.fallbackValidatePrepare);
    pass.dispatchWorkgroups(1);
    pass.setPipeline(this.pipelines.lbvhFallbackValidate);
    pass.setBindGroup(0, this.sparseBindGroups.fallbackValidate);
    pass.dispatchWorkgroupsIndirect(this.buildBuffers.indirectDispatch, 0);
    pass.end();
  }

  private recordSparseFinalizePass(commandEncoder: GPUCommandEncoder): void {
    if (!this.pipelines || !this.sparseBindGroups.finalize) { return; }
    const pass = commandEncoder.beginComputePass({ label: 'LBVH Sparse Refit Finalize' });
    pass.setPipeline(this.pipelines.lbvhSparseFinalize);
    pass.setBindGroup(0, this.sparseBindGroups.finalize);
    pass.dispatchWorkgroups(1);
    pass.end();
  }

  private recordLBVHPass(commandEncoder: GPUCommandEncoder, primCount: number): void {
    if (
      !this.buildBuffers
      || !this.pipelines
      || !this.staticBindGroups.lbvhInitState
      || !this.staticBindGroups.lbvhBuildTopology
      || !this.staticBindGroups.lbvhSeedInternal
      || !this.staticBindGroups.lbvhRefitWave0to1
      || !this.staticBindGroups.lbvhRefitWave1to0
      || !this.staticBindGroups.lbvhUpdateDispatch0to1
      || !this.staticBindGroups.lbvhUpdateDispatch1to0
      || !this.staticBindGroups.lbvhFinalize
    ) {
      return;
    }

    this.device.queue.writeBuffer(
      this.buildBuffers.uniforms,
      UNIFORM_LBVH_OFFSET,
      new Uint32Array([primCount, 0, 0, 0]),
    );

    commandEncoder.clearBuffer(this.buildBuffers.activeCount0);
    commandEncoder.clearBuffer(this.buildBuffers.activeCount1);

    const pass = commandEncoder.beginComputePass({ label: 'LBVH Topology Pass' });

    pass.setPipeline(this.pipelines.lbvhInitState);
    pass.setBindGroup(0, this.staticBindGroups.lbvhInitState);
    pass.dispatchWorkgroups(Math.ceil((primCount * 2) / BUILD_WORKGROUP_SIZE));

    if (primCount > 1) {
      pass.setPipeline(this.pipelines.lbvhBuildTopology);
      pass.setBindGroup(0, this.staticBindGroups.lbvhBuildTopology);
      pass.dispatchWorkgroups(Math.ceil((primCount - 1) / BUILD_WORKGROUP_SIZE));

      pass.setPipeline(this.pipelines.lbvhSeedInternal);
      pass.setBindGroup(0, this.staticBindGroups.lbvhSeedInternal);
      pass.dispatchWorkgroups(Math.ceil(primCount / BUILD_WORKGROUP_SIZE));

      const maxIterations = this.getRefitMaxIterations(primCount);
      for (let iter = 0; iter < maxIterations; iter++) {
        const even = (iter & 1) === 0;
        pass.setPipeline(this.pipelines.lbvhUpdateDispatch);
        pass.setBindGroup(
          0,
          even ? this.staticBindGroups.lbvhUpdateDispatch0to1 : this.staticBindGroups.lbvhUpdateDispatch1to0,
        );
        pass.dispatchWorkgroups(1);

        pass.setPipeline(this.pipelines.lbvhRefitWave);
        pass.setBindGroup(
          0,
          even ? this.staticBindGroups.lbvhRefitWave0to1 : this.staticBindGroups.lbvhRefitWave1to0,
        );
        pass.dispatchWorkgroupsIndirect(this.buildBuffers.indirectDispatch, 0);
      }
    }

    pass.setPipeline(this.pipelines.lbvhFinalize);
    pass.setBindGroup(0, this.staticBindGroups.lbvhFinalize);
    pass.dispatchWorkgroups(1);

    pass.end();
  }

  private recordQualityPass(
    commandEncoder: GPUCommandEncoder,
    primCount: number,
    establishBaseline: boolean,
  ): void {
    if (
      !this.pipelines
      || !this.staticBindGroups.lbvhQualityProbe
      || !this.staticBindGroups.lbvhQualityFinalizeBaseline
      || !this.staticBindGroups.lbvhQualityFinalizeSample
    ) {
      return;
    }

    const pass = commandEncoder.beginComputePass({ label: 'LBVH Quality Pass' });
    if (primCount > 1) {
      pass.setPipeline(this.pipelines.lbvhQualityProbe);
      pass.setBindGroup(0, this.staticBindGroups.lbvhQualityProbe);
      pass.dispatchWorkgroups(Math.ceil((primCount - 1) / BUILD_WORKGROUP_SIZE));
    }
    if (establishBaseline) {
      pass.setPipeline(this.pipelines.lbvhQualityFinalizeBaseline);
      pass.setBindGroup(0, this.staticBindGroups.lbvhQualityFinalizeBaseline);
    } else {
      pass.setPipeline(this.pipelines.lbvhQualityFinalizeSample);
      pass.setBindGroup(0, this.staticBindGroups.lbvhQualityFinalizeSample);
    }
    pass.dispatchWorkgroups(1);
    pass.end();
  }

  private getRefitMaxIterations(primCount: number): number {
    if (primCount <= 1) {
      return 1;
    }
    return Math.max(1, Math.ceil(Math.log2(primCount) * LBVH_REFIT_MAX_ITERATION_FACTOR));
  }

  private getRefitIterationLimit(primCount: number): number {
    const safeLimit = this.getRefitMaxIterations(primCount);
    if (this.measuredRefitIterations === null) {
      return safeLimit;
    }
    return Math.max(1, Math.min(safeLimit, this.measuredRefitIterations));
  }

  private readRefitDepthAsync(
    readback: GPUBuffer,
    topologyGeneration: number,
    safeLimit: number,
  ): Promise<void> {
    return readback.mapAsync(GPUMapMode.READ).then(() => {
      const depth = new Uint32Array(readback.getMappedRange().slice(0))[0] ?? 0;
      if (
        !this.disposed
        && topologyGeneration === this.topologyGeneration
        && depth > 0
        // Hitting the conservative cap cannot distinguish exact convergence
        // from a topology that would need one more wave. In that case retain
        // the established safe limit instead of treating it as measured.
        && depth < safeLimit
      ) {
        this.measuredRefitIterations = depth;
      }
    }).catch((error: unknown) => {
      if (!this.disposed && topologyGeneration === this.topologyGeneration) {
        console.warn('LBVH refit-depth readback failed; retaining the safe wave limit.', error);
      }
    }).finally(() => {
      if (readback.mapState === 'mapped') {
        readback.unmap();
      }
      readback.destroy();
    });
  }

  private nextPowerOf2(value: number): number {
    let v = Math.max(1, value | 0);
    v--;
    v |= v >> 1;
    v |= v >> 2;
    v |= v >> 4;
    v |= v >> 8;
    v |= v >> 16;
    v++;
    return v;
  }
}
